// Pub/sub bridge between coreApiClient (api.js) and EC2WakeOverlay, plus a
// shared "wake cycle" promise so a request that failed because the backend
// EC2 instance was stopped (idle auto-stop) can be replayed once it's back,
// instead of the caller just seeing an error (see api.js's _replayAfterWake).
//
// Multiple requests can fail concurrently while the instance is down (other
// tabs, native app, this tab) - they all share the same wake cycle/promise
// and each independently replays once it resolves. That means their replays
// can race each other, but that race already exists any time two tabs are
// open against a live backend, so it's not a new risk introduced here.

const listeners = new Set();

let pendingWake = null; // { promise, resolve, reject } for the in-flight wake cycle, if any

export const computeWake = {
  notifyUnreachable: () => {
    listeners.forEach((fn) => fn());
  },
  subscribe: (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  // Triggers EC2WakeOverlay (if not already open) and returns a promise that
  // resolves once it confirms the backend is healthy again, or rejects if the
  // user closes the popup first. Concurrent callers share the same promise.
  waitForWake: () => {
    if (!pendingWake) {
      let resolve, reject;
      const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
      pendingWake = { promise, resolve, reject };
    }
    computeWake.notifyUnreachable();
    return pendingWake.promise;
  },

  // EC2WakeOverlay calls this once /system/compute-status reports healthy.
  resolveWake: () => {
    if (pendingWake) {
      pendingWake.resolve();
      pendingWake = null;
    }
  },

  // EC2WakeOverlay calls this when the user closes the popup before ready -
  // every request waiting on this wake cycle falls back to its original error.
  cancelWake: () => {
    if (pendingWake) {
      pendingWake.reject(new Error('EC2 wake cancelled by user'));
      pendingWake = null;
    }
  },
};
